import React, { ReactElement } from 'react';
import styled from 'styled-components';
import Provider from '@solvhealth/types/interfaces/Provider';
import Specialty from '@solvhealth/types/interfaces/Specialty';
import { smallerThanOrEqualTo } from '../../../../core/util/styledComponents';
import { getImageObject } from '../../Provider/util';
import { getProviderName } from '../../Provider/util';
import Picture from '../../../SolvPatternLibrary/Picture';
import useFetchSpecialty from '../../hooks/useFetchSpecialty';

type Props = {
  provider: Provider;
  className?: string;
  responsiveSize?: [mobileSize: string, desktopSize: string];
};

const Root = styled.img.withConfig({ componentId: 'sc-hp5bch-0' })`
  border-radius: 50%;
`;

const PictureContainer = styled(({ desktopSize, mobileSize, ...rest }) => <div {...rest} />).withConfig({ componentId: 'sc-hp5bch-1' })`
  img {
    width: ${(props) => props.desktopSize};
    height: ${(props) => props.desktopSize};
    ${smallerThanOrEqualTo.sm<{ mobileSize: string }>`
      width: ${(props) => props.mobileSize};
      height: ${(props) => props.mobileSize};
      margin: 0 auto;
  `}
  }
`;

export const getProviderPhotoDescription = (provider: Provider, specialty: Specialty) => {
  const name = getProviderName(provider, true, true);
  return `${name}${specialty && ` - ${specialty.display_name}`}`;
};

const Photo = ({ provider, className, responsiveSize = ['80px', '100px'] }: Props) => {
  const specialty = useFetchSpecialty(provider?.specialties?.[0]);
  const description = getProviderPhotoDescription(provider, specialty);
  const imageObject = getImageObject(provider);

  return (
    <span itemProp="image" itemScope itemType="https://health-lifesci.schema.org/ImageObject">
      <meta content={getProviderName(provider)} itemProp="name" />
      <meta content={imageObject.fullRes} itemProp="contentUrl" />
      <meta content={imageObject.fullRes} itemProp="url" />
      <meta content={description} itemProp="Description" />
      <PictureContainer desktopSize={responsiveSize[1]} mobileSize={responsiveSize[0]}>
        <Picture
          alt={description}
          className={className}
          imageObject={imageObject}
          imgTag={Root}
          itemProp="image"
          role="presentation"
        />
      </PictureContainer>
    </span>
  );
};

export default Photo;
